/**
 * push-subscription — helpers Web Push API no frontend (R6.5 / FRENTE E).
 *
 * Fluxo de subscribe:
 *   1. requestPushPermission()
 *      - Verifica suporte (Notification + PushManager + SW)
 *      - Pede permission via Notification.requestPermission()
 *      - registration.pushManager.subscribe() com VAPID public key
 *      - POST /api/v1/push/subscribe pra persistir no backend
 *      - Retorna PushStatus
 *
 *   2. unsubscribeFromPush()
 *      - Unsubscribe do browser pushManager
 *      - DELETE /api/v1/push/subscribe (passa endpoint)
 *
 *   3. getPushStatus()
 *      - Checa estado atual (permission + subscription ativa + suporte)
 *      - GET /api/v1/push/status (backend confirma quantos devices)
 *
 * iOS specifics:
 *   - iOS 16.4+ é o primeiro com Web Push, e SÓ se app instalado como PWA.
 *   - getPushStatus detecta iOS+não-instalado e retorna unsupported='ios-needs-install'.
 *
 * NÃO REVERTER:
 *   - urlBase64ToUint8Array é OBRIGATÓRIO — applicationServerKey precisa
 *     de Uint8Array, não string base64url.
 *   - userVisibleOnly:true é OBRIGATÓRIO pelo spec — sem isso navegador
 *     rejeita subscribe.
 *   - arrayBufferToBase64 com btoa+Uint8Array compat ampla (sem Buffer).
 */

import { api } from './api';

// ─── Tipos públicos ────────────────────────────────────────────────

export type PushUnsupportedReason =
  | 'no-notification-api' // navegador antigo sem Notification API
  | 'no-push-manager' // navegador sem PushManager
  | 'no-service-worker' // SW não suportado
  | 'no-vapid-key' // VAPID public key não disponível (env ausente)
  | 'permission-denied' // user clicou "Block" no permission dialog
  | 'ios-needs-install'; // iOS Safari sem PWA instalado

export interface PushStatus {
  supported: boolean;
  unsupportedReason?: PushUnsupportedReason;
  permission: NotificationPermission | 'unsupported';
  /** Subscription ativa no browser atual. */
  subscribedInBrowser: boolean;
  /** Total de devices inscritos pelo user (backend count). */
  activeDevicesBackend: number;
  /** Backend tem VAPID configurado? */
  pushEnabledBackend: boolean;
}

interface PushBackendStatus {
  pushEnabled: boolean;
  activeDevices: number;
  vapidPublicKey: string | null;
}

// ─── Helpers internos ──────────────────────────────────────────────

/**
 * VAPID public key vem base64url. pushManager.subscribe exige Uint8Array.
 * Algoritmo padrão (Mozilla docs).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function isIOSDevice(): boolean {
  const ua = window.navigator.userAgent;
  const isIPad = /Mac/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || isIPad;
}

function isStandaloneMode(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  return false;
}

/**
 * Resolve VAPID public key — prefer VITE_VAPID_PUBLIC_KEY (build-time),
 * fallback pro endpoint backend.
 */
async function resolveVapidPublicKey(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  try {
    const status = await api.get<PushBackendStatus>('/api/v1/push/status');
    return status.vapidPublicKey;
  } catch {
    return null;
  }
}

// ─── API pública ───────────────────────────────────────────────────

/**
 * Verifica suporte + permission + estado atual da subscription. Não chama
 * permission dialog — apenas leitura.
 */
export async function getPushStatus(): Promise<PushStatus> {
  // 1) Suporte básico
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return {
      supported: false,
      unsupportedReason: 'no-notification-api',
      permission: 'unsupported',
      subscribedInBrowser: false,
      activeDevicesBackend: 0,
      pushEnabledBackend: false,
    };
  }
  if (!('PushManager' in window)) {
    return {
      supported: false,
      unsupportedReason: 'no-push-manager',
      permission: Notification.permission,
      subscribedInBrowser: false,
      activeDevicesBackend: 0,
      pushEnabledBackend: false,
    };
  }
  if (!('serviceWorker' in navigator)) {
    return {
      supported: false,
      unsupportedReason: 'no-service-worker',
      permission: Notification.permission,
      subscribedInBrowser: false,
      activeDevicesBackend: 0,
      pushEnabledBackend: false,
    };
  }
  // iOS Safari só suporta push se app instalado como PWA.
  if (isIOSDevice() && !isStandaloneMode()) {
    return {
      supported: false,
      unsupportedReason: 'ios-needs-install',
      permission: Notification.permission,
      subscribedInBrowser: false,
      activeDevicesBackend: 0,
      pushEnabledBackend: false,
    };
  }

  // 2) Backend status
  let backendStatus: PushBackendStatus = {
    pushEnabled: false,
    activeDevices: 0,
    vapidPublicKey: null,
  };
  try {
    backendStatus = await api.get<PushBackendStatus>('/api/v1/push/status');
  } catch {
    /* sem auth ou backend down — segue com defaults */
  }

  // 3) Subscription ativa no browser?
  let subscribedInBrowser = false;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const sub = await registration.pushManager.getSubscription();
      subscribedInBrowser = !!sub;
    }
  } catch {
    /* tolera — provavelmente SW não registrou ainda */
  }

  return {
    supported: true,
    permission: Notification.permission,
    subscribedInBrowser,
    activeDevicesBackend: backendStatus.activeDevices,
    pushEnabledBackend: backendStatus.pushEnabled,
  };
}

/**
 * Fluxo completo de subscribe — pede permission + cria sub + persiste backend.
 * Retorna PushStatus atualizado.
 *
 * Tratamento de erros:
 *   - User clica "Block" → permission='denied' + Sentry warn (sem throw).
 *   - VAPID indisponível → throw com mensagem clara (backend não configurou).
 *   - SW não registrado ainda → throw (SW deve ter registrado no boot).
 */
export async function requestPushPermission(): Promise<PushStatus> {
  const status = await getPushStatus();
  if (!status.supported) return status;

  // 1) Permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ...status, permission };
  }

  // 2) VAPID key
  const vapidKey = await resolveVapidPublicKey();
  if (!vapidKey) {
    return {
      ...status,
      supported: false,
      unsupportedReason: 'no-vapid-key',
      permission,
    };
  }

  // 3) Service Worker registrado
  const registration = await navigator.serviceWorker.ready;

  // 4) Subscribe (idempotente — pushManager.subscribe é UPSERT do lado do browser)
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    // Cast pra BufferSource — TS narrowing falha com Uint8Array<ArrayBufferLike>
    // mas applicationServerKey aceita ArrayBuffer/ArrayBufferView em runtime.
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // OBRIGATÓRIO pelo spec
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    });
  }

  // 5) Persiste no backend
  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!p256dh || !auth) {
    throw new Error('Subscription sem chaves p256dh/auth — browser não retornou.');
  }
  await api.post('/api/v1/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(p256dh),
      auth: arrayBufferToBase64(auth),
    },
  });

  return getPushStatus();
}

/**
 * Unsubscribe do device atual. Limpa browser + backend (atomic best-effort).
 */
export async function unsubscribeFromPush(): Promise<PushStatus> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return getPushStatus();
  const sub = await registration.pushManager.getSubscription();
  if (!sub) return getPushStatus();

  const endpoint = sub.endpoint;
  // 1) Limpa browser
  try {
    await sub.unsubscribe();
  } catch {
    /* tolera — segue pro backend */
  }
  // 2) Limpa backend (rejeita silenciosamente se 410 já tinha limpado)
  try {
    await api.delete('/api/v1/push/subscribe', { body: { endpoint } } as Parameters<typeof api.delete>[1]);
  } catch {
    /* tolera */
  }
  return getPushStatus();
}
